import { useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Search, Plus, Settings, LogOut, Sparkles } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../../ui/sheet";
import { primaryNav, mobilePrimary, isNavActive } from "./nav-config";
import { useDashboardChrome } from "./dashboard-chrome";
import { AppDispatch, persistor } from "../../../store/store";
import { logout } from "../../../store/authSlice";
import { adminLogout } from "../../../services/api";
import { cn } from "../../../lib/util";

export function MobileNav() {
  const { pathname } = useLocation();
  const { openPalette } = useDashboardChrome();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await adminLogout();
    } catch (e) {
      /* noop */
    } finally {
      dispatch(logout());
      persistor.purge();
      navigate("/");
    }
  };

  return (
    <>
      {/* Top bar */}
      <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-[#EAD7D6] bg-[#FFFBFB]/90 px-4 backdrop-blur lg:hidden">
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              aria-label="Open menu"
              className="flex h-11 w-11 items-center justify-center rounded-full text-[#3a2e2c] transition hover:bg-[#FBEFEF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
            >
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] border-r-[#EAD7D6] bg-[#B87775] p-0 text-white">
            <div className="flex h-16 items-center px-6">
              <img src="/images/whiteLogo.svg" alt="Charme" className="h-8" />
            </div>
            <nav className="px-3 pb-6" aria-label="All sections">
              <p className="px-3 pb-2 text-[10px] font-medium uppercase tracking-[0.22em] text-white/55">
                Run the studio
              </p>
              <ul className="space-y-0.5">
                {primaryNav.map((item) => {
                  const active = isNavActive(pathname, item);
                  const Icon = item.icon;
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        onClick={() => setDrawerOpen(false)}
                        aria-current={active ? "page" : undefined}
                        className={cn(
                          "flex items-center gap-3 rounded-xl px-3 py-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70",
                          active ? "bg-white/20 font-medium" : "hover:bg-black/15"
                        )}
                      >
                        <Icon className="h-5 w-5" />
                        {item.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-3 border-t border-white/15 pt-3">
                <Link
                  to="/control/settings"
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm transition-colors hover:bg-black/15"
                >
                  <Settings className="h-5 w-5" />
                  Settings
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm text-white/85 transition-colors hover:bg-black/15"
                >
                  <LogOut className="h-5 w-5" />
                  Sign out
                </button>
              </div>
            </nav>
          </SheetContent>
        </Sheet>

        <Link to="/control" className="flex items-center" aria-label="Charme admin home">
          <Sparkles className="h-5 w-5 text-[#B87775]" />
          <span className="ml-1.5 font-serif text-lg text-[#3a2e2c]">Charme</span>
        </Link>

        <button
          type="button"
          onClick={openPalette}
          aria-label="Search"
          className="ml-auto flex h-11 w-11 items-center justify-center rounded-full text-[#9A6B69] transition hover:bg-[#FBEFEF] hover:text-[#B87775] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
        >
          <Search className="h-5 w-5" />
        </button>
      </header>

      {/* Bottom tab bar — daily surfaces + quick create */}
      <nav
        aria-label="Primary"
        className="fixed inset-x-0 bottom-0 z-30 flex h-16 items-stretch border-t border-[#EAD7D6] bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden"
      >
        {mobilePrimary.map((item) => {
          const active = isNavActive(pathname, item);
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex flex-1 flex-col items-center justify-center gap-1 text-[11px] transition-colors",
                active ? "text-[#B87775]" : "text-[#9A6B69]"
              )}
            >
              <Icon className={cn("h-5 w-5", active && "stroke-[2.4]")} />
              {item.name}
            </Link>
          );
        })}
        <button
          type="button"
          onClick={openPalette}
          aria-label="Quick create"
          className="flex flex-1 flex-col items-center justify-center gap-1 text-[11px] text-[#9A6B69]"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#B87775] text-white">
            <Plus className="h-4 w-4" />
          </span>
          New
        </button>
      </nav>
    </>
  );
}
